import type { DocumentExtractionV2Invocation } from '@lifeomic/react-client';
import { REVIEWABLE_RESOURCE_TYPES } from '@lifeomic/phc-web-toolkit/src/util/extractedResourceReview';
import type { ReviewableResourceType } from './useUnreviewedExtractedResources';

export type ReviewableResourceRef = {
  resourceType: ReviewableResourceType;
  id: string;
};

type ResourceOutputEntry = {
  resource_type?: string | null;
  resource_id?: string | null;
  status?: string | null;
  role?: string | null;
  dedupe_outcome?: string | null;
};

// Artifact roles whose persisted resource is a reviewable clinical resource.
// This mirrors the established review queue (DocumentExtractionV2Tasks) so the
// "Analyze with Zori AI" modal surfaces exactly the resources that were written
// to FHIR — and nothing that merely reached the projection stage.
const REVIEWABLE_ARTIFACT_ROLES = new Set([
  'clinical-observation',
  'clinical-resource',
]);

// Dedupe outcomes that mean the extraction was accepted for write. Combined with
// a non-persisted projection status, these flag resources that *should* have been
// saved but weren't (e.g. `persistence-failed`).
const ACCEPTED_DEDUPE_OUTCOMES = new Set(['accepted-new', 'matched-existing']);

type ReviewableResourceFilter = {
  /**
   * Resource types to leave out of the review set. Document sources use this to
   * skip Observations, which get their own dedicated section with source-text
   * search rather than a generic review card.
   */
  excludedResourceTypes?: ReadonlySet<ReviewableResourceType>;
};

const isReviewableResourceType = (
  resourceType: string | null | undefined,
  { excludedResourceTypes }: ReviewableResourceFilter = {},
): resourceType is ReviewableResourceType =>
  !!resourceType &&
  REVIEWABLE_RESOURCE_TYPES.has(resourceType) &&
  !excludedResourceTypes?.has(resourceType as ReviewableResourceType);

const getArtifacts = (
  task: DocumentExtractionV2Invocation | null,
): ResourceOutputEntry[] => {
  const fhirArtifacts = task?.output?.fhir_artifacts;
  return Array.isArray(fhirArtifacts) ? fhirArtifacts : [];
};

const getProjections = (
  task: DocumentExtractionV2Invocation | null,
): ResourceOutputEntry[] => {
  const fhirProjections = task?.output?.fhir_projections;
  return Array.isArray(fhirProjections) ? fhirProjections : [];
};

/**
 * Derive the set of persisted clinical resources (across every reviewable FHIR
 * type, not just Observations) that a completed extraction task actually wrote
 * to FHIR, so the "Analyze with Zori AI" modal can hydrate and present them with
 * approve/reject controls. Returns unique `{ resourceType, id }` refs.
 *
 * Only persisted `fhir_artifacts` are considered — a projection reaching
 * `accepted-new` does not guarantee a persisted resource (it can later fail with
 * `persistence-failed`), and hydrating those would surface non-existent
 * resources.
 */
export const getPersistedReviewableResourceRefs = (
  task: DocumentExtractionV2Invocation | null,
  filter: ReviewableResourceFilter = {},
): ReviewableResourceRef[] => {
  const refs: ReviewableResourceRef[] = getArtifacts(task)
    .filter(
      artifact =>
        artifact.status === 'persisted' &&
        REVIEWABLE_ARTIFACT_ROLES.has(artifact.role ?? '') &&
        isReviewableResourceType(artifact.resource_type, filter) &&
        !!artifact.resource_id,
    )
    .map(artifact => ({
      resourceType: artifact.resource_type as ReviewableResourceType,
      id: artifact.resource_id as string,
    }));

  const seen = new Set<string>();
  return refs.filter(ref => {
    const key = `${ref.resourceType}/${ref.id}`;
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

/**
 * Count reviewable clinical resources that the extraction accepted for write but
 * never persisted (e.g. `persistence-failed`). These don't exist in FHIR, so
 * they can't be reviewed — but the reviewer should know they were dropped rather
 * than silently see fewer resources than the summary mentions.
 */
export const getUnpersistedReviewableResourceCount = (
  task: DocumentExtractionV2Invocation | null,
  filter: ReviewableResourceFilter = {},
): number => {
  const persistedKeys = new Set(
    getPersistedReviewableResourceRefs(task, filter).map(
      ref => `${ref.resourceType}/${ref.id}`,
    ),
  );

  const seen = new Set<string>();
  return getProjections(task).filter(projection => {
    if (
      !isReviewableResourceType(projection.resource_type, filter) ||
      !projection.resource_id ||
      projection.status === 'persisted' ||
      !ACCEPTED_DEDUPE_OUTCOMES.has(projection.dedupe_outcome ?? '')
    ) {
      return false;
    }
    const key = `${projection.resource_type}/${projection.resource_id}`;
    if (persistedKeys.has(key) || seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  }).length;
};
